import React from 'react';
import { Card, CardActionArea, Box, Typography, Stack, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';

/**
 * KPI stat card with a gradient icon tile, large value, and optional
 * trend chip or caption. Clickable when `onClick` is provided, with a
 * selected state for filter-style usage.
 */
const StatCard = ({
  label,
  value,
  icon,
  color = 'primary',
  trend,
  trendLabel = 'vs last month',
  caption,
  onClick,
  selected = false,
  sx,
}) => {
  const renderTrend = () => {
    if (trend === undefined || trend === null) return null;
    const rounded = Math.round(trend * 10) / 10;
    const isUp = rounded > 0;
    const isFlat = rounded === 0;
    const TrendIcon = isFlat ? TrendingFlatRoundedIcon : isUp ? TrendingUpRoundedIcon : TrendingDownRoundedIcon;
    const trendColor = isFlat ? 'default' : isUp ? 'success' : 'error';
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          color={trendColor}
          icon={<TrendIcon sx={{ fontSize: '1rem !important' }} />}
          label={`${isUp ? '+' : ''}${rounded}%`}
          sx={{
            fontWeight: 700,
            ...(trendColor !== 'default' && {
              bgcolor: (theme) => alpha(theme.palette[trendColor].main, 0.12),
              color: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette[trendColor].dark
                  : theme.palette[trendColor].light,
              '& .MuiChip-icon': { color: 'inherit' },
            }),
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {trendLabel}
        </Typography>
      </Stack>
    );
  };

  const content = (
    <Box sx={{ p: 2.5, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              display: 'block',
              lineHeight: 1.5,
              fontSize: { xs: '0.62rem', sm: '0.6875rem' },
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: '1.6rem', md: '1.9rem' }, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}
          >
            {value}
          </Typography>
        </Box>
        {icon && (
          <Box
            aria-hidden
            sx={{
              width: 46,
              height: 46,
              borderRadius: '14px',
              display: { xs: 'none', sm: 'grid' },
              placeItems: 'center',
              flexShrink: 0,
              color: `${color}.main`,
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.18)} 0%, ${alpha(
                  theme.palette[color].main,
                  0.06
                )} 100%)`,
              '& svg': { fontSize: 24 },
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
      {renderTrend()}
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );

  const cardSx = {
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
    ...(selected && {
      borderColor: (theme) => alpha(theme.palette[color].main, 0.6),
      boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette[color].main, 0.14)}`,
    }),
    ...(onClick && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: (theme) => theme.custom.hoverShadow,
      },
    }),
    ...sx,
  };

  if (onClick) {
    return (
      <Card sx={cardSx}>
        <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'stretch', display: 'flex' }}>
          {content}
        </CardActionArea>
      </Card>
    );
  }

  return <Card sx={cardSx}>{content}</Card>;
};

export default StatCard;
